import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"
import { useQuery } from "urql"

import IntakeFormRow, {
  VARIANT_SELECTABLE,
  VARIANT_SELECTABLE_ACTIVE,
  VARIANT_SELECTABLE_COMPLETED
} from "./IntakeFormRow"
import { AnimatedModal } from "./Modal"

const PRACTICE_INTAKE_FORMS_QUERY = `
  query PracticeIntakeFormsForModal {
    currentPractice {
      intakeForms {
        id
        name
        isDefault
        status
      }
    }
  }
`

const MODE_ADD = "add"
const MODE_SEND = "send"

export default function IntakeFormSelectModal({
  visible,
  onClose,
  onSubmit,
  existingFormIds = [],
  completedFormIds = [],
  mode = MODE_SEND
}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set())

  const [{ data }] = useQuery({
    query: PRACTICE_INTAKE_FORMS_QUERY,
    requestPolicy: "cache-and-network",
    pause: !visible
  })

  const intakeForms = data?.currentPractice?.intakeForms ?? []
  // In MODE_ADD, forms already on file can still be linked to this appointment via sync_intake_forms!;
  // disabling them blocked pros from attaching forms the client completed on a prior visit.
  const completedSet = useMemo(
    () => (mode === MODE_ADD ? new Set() : new Set(completedFormIds)),
    [mode, completedFormIds]
  )

  const selectableForms = useMemo(() => {
    const active = intakeForms.filter((f) => f.status === "active" || f.status === 1 || f.status === "1")
    if (mode === MODE_ADD && existingFormIds.length > 0) {
      const existingSet = new Set(existingFormIds)
      return active.filter((f) => !existingSet.has(f.id))
    }
    return active
  }, [intakeForms, mode, existingFormIds])

  const toggleForm = (formId) => {
    if (completedSet.has(formId)) return
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(formId)) next.delete(formId)
      else next.add(formId)
      return next
    })
  }

  const selectedCount = selectedIds.size
  const actionLabel =
    mode === MODE_ADD
      ? selectedCount === 1
        ? "Add 1 form"
        : `Add ${selectedCount} form(s)`
      : selectedCount === 1
      ? "Send 1 form"
      : `Send ${selectedCount} form(s)`

  const handleSubmit = () => {
    const selected = selectableForms.filter((f) => selectedIds.has(f.id)).map((f) => ({ id: f.id, name: f.name }))
    onSubmit(selected)
    setSelectedIds(new Set())
    onClose()
  }

  const handleClose = () => {
    setSelectedIds(new Set())
    onClose()
  }

  return (
    <AnimatedModal
      visible={visible}
      hideModal={handleClose}
      header={mode === MODE_ADD ? "Add forms to this appointment" : "Select forms to send"}
      showFooter
      hideSaveButton={false}
      cancelButtonCopy="Close"
      actionButtonCopy={actionLabel}
      onSave={handleSubmit}
      saveDisabled={selectedCount === 0}
      dialogClassName="max-w-[560px] md:max-w-[345px] md:min-w-0 w-full">
      <div className="max-h-[60vh] overflow-y-auto">
        <div className="space-y-2 py-2">
          {selectableForms.length === 0 ? (
            <p className="text-sm text-gray-dark">No active intake forms available.</p>
          ) : (
            selectableForms.map((form) => {
              const isCompleted = completedSet.has(form.id)
              const isSelected = selectedIds.has(form.id)
              const variant = isCompleted
                ? VARIANT_SELECTABLE_COMPLETED
                : isSelected
                ? VARIANT_SELECTABLE_ACTIVE
                : VARIANT_SELECTABLE

              return <IntakeFormRow key={form.id} form={form} variant={variant} onToggle={toggleForm} />
            })
          )}
        </div>
      </div>
    </AnimatedModal>
  )
}

IntakeFormSelectModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  existingFormIds: PropTypes.arrayOf(PropTypes.string),
  completedFormIds: PropTypes.arrayOf(PropTypes.string),
  mode: PropTypes.oneOf([MODE_ADD, MODE_SEND])
}

export { MODE_ADD, MODE_SEND }
